// ============================================
// COMPONENT: EquipmentModal - CORREGIDO
// ============================================
// Archivo: src/components/modals/EquipmentModal.jsx

import React, { useState, useEffect } from 'react';
import { Modal, Input, Select, Button } from '../common';

const EquipmentModal = ({ 
  show, 
  equipment = null,
  plantId,
  line,
  onClose, 
  onSubmit 
}) => {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    manufacturer: '',
    model: '',
    serial: '',
    cost: '',
    status: 'operativo',
    installdate: '',
    supplier: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const isEdit = !!equipment;

  useEffect(() => {
    if (equipment) {
      setFormData({
        code: equipment.code || '',
        name: equipment.name || '',
        manufacturer: equipment.manufacturer || '',
        model: equipment.model || '',
        serial: equipment.serial || '',
        cost: equipment.cost || '',
        status: equipment.status || 'operativo',
        installdate: equipment.installdate 
        ? new Date(equipment.installdate).toISOString().split('T')[0] 
        : '',
        supplier: equipment.supplier || ''
      });
    } else {
      setFormData({
        code: '',
        name: '',
        manufacturer: '',
        model: '',
        serial: '',
        cost: '',
        status: 'operativo',
        installdate: '',
        supplier: ''
      });
    }
    setErrors({});
  }, [equipment, show]);

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre del equipo es obligatorio';
    }

    if (formData.cost && isNaN(formData.cost)) {
      newErrors.cost = 'El costo debe ser un número válido';
    }

    if (!plantId) {
      newErrors.general = 'ID de planta no proporcionado';
    }

    if (!line) {
      newErrors.general = 'Línea no proporcionada';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

      

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        plant_id: plantId,
        name: formData.name,
        code: formData.code,
        manufacturer: formData.manufacturer,
        model: formData.model,
        serial: formData.serial,
        cost: parseFloat(formData.cost) || 0,
        status: formData.status,
        line: line,
        installdate: formData.installdate || null,
        supplier: formData.supplier
      };
      console.log('🔍 plantId:', plantId);
      console.log('🔍 line:', line);
      console.log('🔍 submitData:', submitData);
      await onSubmit(submitData, equipment?.id);
      onClose();
    } catch (error) {
      console.error('Error al guardar equipo:', error);
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    { value: 'operativo', label: 'Operativo' },
    { value: 'falla', label: 'Con Falla' },
    { value: 'en_falla', label: 'En Falla' },
    { value: 'mantenimiento', label: 'En Mantenimiento' },
    { value: 'deshabilitado', label: 'Deshabilitado' }
  ];

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={isEdit ? 'Editar Equipo' : `Nuevo Equipo - Línea ${line}`}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {errors.general && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
            {errors.general}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          <Input
            label="Código de Equipo"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            placeholder="Ej: EQ-001"
            required
            error={errors.code}
          />

          <Input
            label="Nombre del Equipo"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Ej: Freno Dinamómetro"
            required
            error={errors.name}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Input
            label="Fabricante"
            value={formData.manufacturer}
            onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
            placeholder="Ej: MAHA"
            required
            error={errors.manufacturer}
          />

          <Input
            label="Modelo"
            value={formData.model}
            onChange={(e) => setFormData({ ...formData, model: e.target.value })}
            placeholder="Ej: IW7"
            required
            error={errors.model}
          />
        </div>

        <Input
          label="Número de Serie"
          value={formData.serial}
          onChange={(e) => setFormData({ ...formData, serial: e.target.value })}
          placeholder="Ej: FD-001"
          error={errors.serial}
        />

        <div className="grid md:grid-cols-2 gap-4">
          <Input
            label="Costo (CLP)"
            type="number"
            value={formData.cost}
            onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
            placeholder="Ej: 15000000"
            error={errors.cost}
            min={0}
          />

          <Select
            label="Estado"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            options={statusOptions}
            required
          /> 

          <Input
            label="Fecha de Instalación"
            type="date"
            value={formData.installdate}
            onChange={(e) => setFormData({ ...formData, installdate: e.target.value })}
            placeholder="Ej: 2023-01-15"
          />

          <Input
            label="Proveedor/Suplidor"
            value={formData.supplier}
            onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
            placeholder="Ej: Proveedor XYZ"
          />
        </div>

        {formData.cost && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
            <p className="text-blue-900">
              <strong>Costo formateado:</strong> ${parseFloat(formData.cost || 0).toLocaleString('es-CL')}
            </p>
          </div>
        )}

        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            fullWidth
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            fullWidth
            disabled={loading}
          >
            {loading ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear Equipo'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default EquipmentModal;